import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

function formatName(str: string) {
  return str.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

export function RouteMetaUpdater() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    let title = 'AMGOC US LLC - AI-Driven Digital Transformation';
    let desc = 'AMGOC US LLC is a full-service digital transformation company offering custom software engineering, AI/ML solutions, SaaS platforms, and offshore talent.';
    let noindex = false;

    const exactMatches: Record<string, { title: string, description: string, noindex?: boolean }> = {
      '/': {
        title: 'AMGOC US | AI-Driven Enterprise Solutions & IT Consulting',
        description: 'Transform your business with AMGOC US. We provide AI-driven software, predictive analytics, and enterprise IT solutions for modern digital transformation.'
      },
      '/about': {
        title: 'About AMGOC US | Enterprise AI & Software Innovation',
        description: 'Learn about AMGOC US, a premier provider of AI-driven solutions, custom software development, and strategic IT consulting for global enterprises.'
      },
      '/contact': {
        title: 'Contact AMGOC US | Request an AI & IT Consultation',
        description: 'Get in touch with AMGOC US. Schedule a demo for MeetingSense, reAIcoach, or discuss custom software development and IT staff augmentation services.'
      },
      '/portfolio': {
        title: 'Our Work & Case Studies | AMGOC US Portfolio',
        description: 'Explore the AMGOC US portfolio. See how our custom software, AI integrations, and digital transformation strategies drive success for enterprise clients.'
      },
      '/thank-you': {
        title: 'Thank You | AMGOC US',
        description: 'Thank you for contacting AMGOC US. Our team of enterprise AI and IT experts will be in touch with you shortly.',
        noindex: true
      },
      '/products': {
        title: 'AI Products & Enterprise Software Solutions | AMGOC US',
        description: 'Discover AMGOC US products. From reAIcoach to MeetingSense, explore our suite of enterprise application software and agentic AI platforms.'
      },
      '/products/reaicoach': {
        title: 'reAIcoach: AI Real Estate Sales & Coaching Platform | AMGOC US',
        description: 'Elevate real estate sales with reAIcoach. Discover AI-powered roleplay, pitch optimization, and predictive lead scoring for agents and top brokerages.'
      },
      '/services': {
        title: 'IT Consulting & Custom Software Services | AMGOC US',
        description: 'Explore AMGOC US services. We offer custom software development, IT staff augmentation, and AI integration to accelerate your digital transformation.'
      },
      '/solutions': {
        title: 'Enterprise AI Solutions & Workflow Automation | AMGOC US',
        description: 'Browse AMGOC US enterprise solutions. We specialize in workflow automation, predictive intelligence, and multi-agent AI systems for complex business challenges.'
      },
      '/solutions/meetingsense': {
        title: 'MeetingSense AI: Automate Meeting Minutes & Tasks | AMGOC US',
        description: 'Stop taking manual notes. MeetingSense uses multi-modal AI to automate attendance, minutes, and task assignments with 100% accountability.'
      },
      '/solutions/inventory-forecasting': {
        title: 'AI Inventory Forecasting & Supply Chain Solutions | AMGOC US',
        description: 'Optimize your supply chain with AMGOC US AI inventory forecasting. Leverage predictive analytics to reduce costs, prevent stockouts, and maximize ROI.'
      },
      '/solutions/environmental-intelligence': {
        title: 'Environmental Intelligence & Geospatial AI | AMGOC US',
        description: 'Leverage geospatial AI and environmental intelligence from AMGOC US. Transform complex environmental data into actionable, sustainable business insights.'
      },
      '/technology': {
        title: 'Technology Stack & AI Capabilities | AMGOC US',
        description: 'Explore the technologies powering AMGOC US. From machine learning frameworks to secure cloud-native infrastructure, discover our technical expertise.'
      },
      '/insights': {
        title: 'AI & Digital Transformation Insights | AMGOC US',
        description: 'Read the latest insights from AMGOC US. Stay updated on enterprise AI trends, digital transformation strategies, and custom software innovations.'
      },
      '/blog': {
        title: 'The AMGOC US Blog | Tech, AI & Business Articles',
        description: 'Browse the AMGOC US blog for expert articles on artificial intelligence, software development, IT consulting, and enterprise technology trends.'
      },
      '/privacy-policy': {
        title: 'Privacy Policy | AMGOC US',
        description: 'Read the AMGOC US Privacy Policy to understand how we collect, use, and protect your data across our enterprise software and AI platforms.'
      },
      '/terms-of-service': {
        title: 'Terms of Service | AMGOC US',
        description: 'Review the Terms of Service for AMGOC US. These terms govern your use of our custom software, AI solutions, and digital platforms.'
      }
    };

    if (exactMatches[path]) {
      title = exactMatches[path].title;
      desc = exactMatches[path].description;
      noindex = exactMatches[path].noindex || false;
    } else {
      let match;
      if ((match = path.match(/^\/products\/([^/]+)$/))) {
        title = `${formatName(match[1])} - Enterprise AI Software | AMGOC US`;
        desc = `Learn more about ${formatName(match[1])} by AMGOC US. Discover how our specialized AI software and enterprise solutions drive operational efficiency and growth.`;
      } else if ((match = path.match(/^\/services\/([^/]+)$/))) {
        title = `${formatName(match[1])} Services & Consulting | AMGOC US`;
        desc = `Discover AMGOC US's ${formatName(match[1])} solutions. We provide tailored, scalable, and secure technology services to meet your enterprise business requirements.`;
      } else if ((match = path.match(/^\/solutions\/industry(?:\/([^/]+))?$/))) {
        const category = match[1] ? formatName(match[1]) : 'Industry';
        title = `AI & IT Solutions for ${category} | AMGOC US`;
        desc = `Discover customized digital transformation and AI solutions tailored for the ${category} sector by the enterprise technology experts at AMGOC US.`;
      } else if ((match = path.match(/^\/solutions\/app-development\/([^/]+)$/))) {
        title = `Custom ${formatName(match[1])} Application Development | AMGOC US`;
        desc = `Partner with AMGOC US for secure, scalable ${formatName(match[1])} application development. We build enterprise-grade mobile and web apps tailored to your business.`;
      } else if ((match = path.match(/^\/solutions\/social\/([^/]+)$/))) {
        title = `${formatName(match[1])} Social Media & Digital Solutions | AMGOC US`;
        desc = `Enhance your digital presence with AMGOC US social media solutions. Leverage data-driven strategies for ${formatName(match[1])} to engage audiences and drive growth.`;
      } else if ((match = path.match(/^\/technology\/([^/]+)$/))) {
        title = `${formatName(match[1])} Integration & Development | AMGOC US`;
        desc = `Leverage ${formatName(match[1])} with AMGOC US. Our expert developers build secure, scalable enterprise solutions utilizing the latest ${formatName(match[1])} frameworks.`;
      } else if ((match = path.match(/^\/blog\/label\/([^/]+)$/))) {
        title = 'The AMGOC US Blog | Tech, AI & Business Articles';
        desc = 'Browse the AMGOC US blog for expert articles on artificial intelligence, software development, IT consulting, and enterprise technology trends.';
      } else if (path.startsWith('/blog/')) {
        // Just generic for client side since we don't have the post data synchronously here
        // The server-side HTML will have the correct exact title.
      } else {
        title = 'Page Not Found | AMGOC US';
        desc = 'The page you are looking for cannot be found. Return to the AMGOC US homepage to explore our AI solutions and custom software services.';
      }
    }

    document.title = title;
    
    // Update description meta tag
    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', desc);
    } else {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      metaDesc.setAttribute('content', desc);
      document.head.appendChild(metaDesc);
    }
    
    // Update robots meta tag
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (noindex) {
      if (metaRobots) {
        metaRobots.setAttribute('content', 'noindex, nofollow');
      } else {
        metaRobots = document.createElement('meta');
        metaRobots.setAttribute('name', 'robots');
        metaRobots.setAttribute('content', 'noindex, nofollow');
        document.head.appendChild(metaRobots);
      }
    } else {
      if (metaRobots) {
        // Remove noindex for other pages if it was added
        metaRobots.remove();
      }
    }

  }, [location.pathname]);

  return null;
}
